import React from 'react';
import { CircleCheck, Lock } from 'lucide-react';
import { FieldState, FormState } from 'formstate';
import { observer } from 'mobx-react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { DateTimePicker } from '@components/date-picker';
import { ImageUpload } from '@components/image-upload';
import { CommunityMultiSelect } from '@components/multi-select/community-multi-select';
import { useImageUpload } from '@components/hooks/use-image-upload';
import { getCdnImage } from '@components/utils/cdn-image';
import { required, useModel } from '@core/forms';
import { extractApiErrorMessage } from '@core/utils/errors';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import {
    CdnObjectDto,
    CommunityPageEntrySupportDto,
    ImageType,
    ScheduledPostDto,
    ScheduledPostRequestDto,
    ScheduledPostStatus,
    ScheduledPostTargetDto
} from '@api/ChallengeClient';
import { PostEditor } from './post-editor';
import { PostAuthor, UserSelect } from '@components/user-select';

type PublishMode = 'now' | 'later';

const GLOBAL_ACCOUNT_AUTHOR: PostAuthor = {
    userId: 'mrs-move',
    name: 'Ms. Move'
};

interface MembershipValidation {
    missingCommunities: { id: number; name: string }[];
    mode: 'blocking' | 'warning';
}

interface PostModalProps {
    open: boolean;
    post?: ScheduledPostDto | null;
    duplicateOf?: ScheduledPostDto | null;
    readOnly?: boolean;
    onClose(): void;
    onSaved(): void;
}

function toCommunityOption(
    target: ScheduledPostTargetDto
): Pick<CommunityPageEntrySupportDto, 'id' | 'name'> {
    return {
        id: target.communityId,
        name: target.communityName
    };
}

export const PostModal = observer(
    ({
        open,
        post,
        duplicateOf,
        readOnly,
        onClose,
        onSaved
    }: PostModalProps) => {
        const { challengeClient } = injectTSDI(Api);
        const source = post ?? duplicateOf ?? null;
        const title = readOnly
            ? 'View feed post'
            : post
              ? 'Edit feed post'
              : 'Create feed post';

        const model = useModel(
            () => {
                const form = new FormState({
                    author: new FieldState<PostAuthor | null>(
                        source
                            ? {
                                  userId: source.authorUserId,
                                  name: source.authorName
                              }
                            : null
                    ).validators(required()),
                    content: new FieldState<string>(
                        source?.contentMarkdown ?? ''
                    ).validators(required()),
                    image: new FieldState<CdnObjectDto | null>(
                        source?.image ?? null
                    ),
                    communityIds: new FieldState<number[]>(
                        source?.targets.map((t) => t.communityId) ?? []
                    ),
                    publishMode: new FieldState<PublishMode>('later'),
                    publishAt: new FieldState<Date | null>(
                        post?.publishedDate
                            ? new Date(post.publishedDate)
                            : null
                    )
                });

                return {
                    form,
                    fields: {
                        author: form.$.author,
                        content: form.$.content,
                        image: form.$.image,
                        communityIds: form.$.communityIds,
                        publishMode: form.$.publishMode,
                        publishAt: form.$.publishAt
                    }
                };
            },
            undefined,
            [post?.id, duplicateOf?.id, open]
        );

        const [communities, setCommunities] = React.useState<
            Pick<CommunityPageEntrySupportDto, 'id' | 'name'>[]
        >(source?.targets.map(toCommunityOption) ?? []);
        const [imageRemoved, setImageRemoved] = React.useState(false);
        const [submitting, setSubmitting] = React.useState(false);
        const [error, setError] = React.useState<string | null>(null);
        const [savedPostId, setSavedPostId] = React.useState<number | null>(
            null
        );
        const submittingRef = React.useRef(false);
        const [membership, setMembership] =
            React.useState<MembershipValidation | null>(null);
        const [membershipResolved, setMembershipResolved] =
            React.useState(false);
        const hadInvalidMembership = React.useRef(false);
        const validationTokenRef = React.useRef(0);

        const validateMembership = async (
            mode: 'blocking' | 'warning'
        ): Promise<boolean> => {
            const token = ++validationTokenRef.current;
            const author = model.fields.author.value;
            const communityIds = model.fields.communityIds.value;
            if (!author || communityIds.length === 0) {
                setMembership(null);
                return true;
            }
            try {
                const response =
                    await challengeClient.support.scheduledPostSupportControllerValidateAuthorMembership(
                        { authorUserId: author.userId, communityIds }
                    );
                if (token !== validationTokenRef.current) {
                    return false;
                }
                if (response.data.valid) {
                    if (hadInvalidMembership.current) {
                        setMembershipResolved(true);
                    }
                    setMembership(null);
                    return true;
                }
                hadInvalidMembership.current = true;
                setMembershipResolved(false);
                setMembership({
                    missingCommunities: response.data.missingCommunities,
                    mode
                });
                return false;
            } catch (err) {
                console.error('Error validating author membership:', err);
                if (token !== validationTokenRef.current) {
                    return false;
                }
                return true;
            }
        };

        React.useEffect(() => {
            validationTokenRef.current++;
            setCommunities(source?.targets.map(toCommunityOption) ?? []);
            setImageRemoved(false);
            setError(null);
            setSavedPostId(null);
            setMembership(null);
            setMembershipResolved(false);
            hadInvalidMembership.current = false;
            if (
                !readOnly &&
                post &&
                (post.status === ScheduledPostStatus.DRAFT ||
                    post.status === ScheduledPostStatus.PLANNED) &&
                post.authorUserId &&
                post.targets.length > 0
            ) {
                void validateMembership('warning');
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [post?.id, duplicateOf?.id, open]);

        React.useEffect(() => {
            if (!membership) {
                return;
            }
            const author = model.fields.author.value;
            const communityIds = model.fields.communityIds.value;
            if (!author || communityIds.length === 0) {
                setMembership(null);
                return;
            }
            void validateMembership(membership.mode);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [model.fields.author.value, model.fields.communityIds.value]);

        const imageUpload = useImageUpload(ImageType.FEED_POST, (cdn) => {
            model.fields.image.onChange(cdn);
            setImageRemoved(false);
        });

        const storedImageUrl = model.fields.image.value
            ? (getCdnImage(
                  model.fields.image.value.bucketName,
                  model.fields.image.value.objectKey,
                  { resize: { width: 800 } }
              ) ?? undefined)
            : undefined;

        const displayedImage = imageRemoved
            ? undefined
            : (imageUpload.previewUrl ?? storedImageUrl);

        const handleRemoveImage = () => {
            model.fields.image.onChange(null);
            setImageRemoved(true);
        };

        const handleClose = () => {
            if (submitting) {
                return;
            }
            onClose();
        };

        const useGlobalAccountAsAuthor = () => {
            model.fields.author.onChange(GLOBAL_ACCOUNT_AUTHOR);
        };

        const missingCommunityIds =
            membership?.missingCommunities.map((c) => c.id) ?? [];
        const memberCommunityIds = membership
            ? communities
                  .map((c) => c.id)
                  .filter((id) => !missingCommunityIds.includes(id))
            : [];

        const buildRequest = (): ScheduledPostRequestDto => {
            const author = model.fields.author.value;
            return {
                contentMarkdown: model.fields.content.value,
                authorUserId: author?.userId ?? '',
                authorName: author?.name ?? '',
                image: model.fields.image.value ?? undefined,
                communityIds: model.fields.communityIds.value
            };
        };

        const persistPost = async (): Promise<number> => {
            const request = buildRequest();
            const existingId = post?.id ?? savedPostId;
            if (existingId) {
                await challengeClient.support.scheduledPostSupportControllerUpdate(
                    existingId,
                    request
                );
                return existingId;
            }
            const response =
                await challengeClient.support.scheduledPostSupportControllerCreate(
                    request
                );
            setSavedPostId(response.data.id);
            return response.data.id;
        };

        const handleSaveDraft = async () => {
            if (submitting || submittingRef.current) {
                return;
            }
            model.fields.content.validators(required());
            model.fields.communityIds.validators((ids: number[]) =>
                ids.length > 0 ? false : 'At least one community is required'
            );
            model.fields.publishAt.validators();

            const validation = await model.form.validate();
            if (validation.hasError) {
                return;
            }

            submittingRef.current = true;
            setSubmitting(true);
            setError(null);
            try {
                await persistPost();
                const validMembership = await validateMembership('warning');
                onSaved();
                if (validMembership) {
                    onClose();
                }
            } catch (err) {
                setError(extractApiErrorMessage(err, 'Failed to save post'));
            } finally {
                submittingRef.current = false;
                setSubmitting(false);
            }
        };

        const handlePublish = async () => {
            if (submitting || submittingRef.current) {
                return;
            }
            model.fields.content.validators(required());
            model.fields.communityIds.validators((ids: number[]) =>
                ids.length > 0 ? false : 'At least one community is required'
            );
            if (model.fields.publishMode.value === 'later') {
                model.fields.publishAt.validators((date: Date | null) => {
                    if (!date) {
                        return 'Publish date is required';
                    }
                    return date.getTime() > Date.now()
                        ? false
                        : 'Publish date must be in the future';
                });
            } else {
                model.fields.publishAt.validators();
            }

            const validation = await model.form.validate();
            if (validation.hasError) {
                return;
            }

            const validMembership = await validateMembership('blocking');
            if (!validMembership) {
                return;
            }

            submittingRef.current = true;
            setSubmitting(true);
            setError(null);
            let id: number;
            try {
                id = await persistPost();
            } catch (err) {
                setError(extractApiErrorMessage(err, 'Failed to save post'));
                submittingRef.current = false;
                setSubmitting(false);
                return;
            }
            try {
                const mode = model.fields.publishMode.value;
                await challengeClient.support.scheduledPostSupportControllerSchedule(
                    id,
                    {
                        publishNow: mode === 'now',
                        publishedDate:
                            mode === 'later' && model.fields.publishAt.value
                                ? model.fields.publishAt.value.toISOString()
                                : undefined
                    }
                );
                onSaved();
                onClose();
            } catch (err) {
                setError(
                    extractApiErrorMessage(err, 'Failed to schedule post')
                );
                onSaved();
            } finally {
                submittingRef.current = false;
                setSubmitting(false);
            }
        };

        return (
            <Dialog
                open={open}
                onOpenChange={(next) => {
                    if (!next) {
                        handleClose();
                    }
                }}
            >
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="flex flex-col gap-4">
                        <UserSelect
                            value={model.fields.author.value}
                            onChange={(value) =>
                                model.fields.author.onChange(value)
                            }
                            error={model.fields.author.error}
                            disabled={readOnly}
                            restoreKey={open}
                        />
                        <PostEditor
                            value={model.fields.content.value}
                            onChange={(value) =>
                                model.fields.content.onChange(value)
                            }
                            error={model.fields.content.error}
                            disabled={readOnly}
                        />
                        {readOnly ? (
                            storedImageUrl && (
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-sm text-muted-foreground">
                                        Image
                                    </span>
                                    <img
                                        src={storedImageUrl}
                                        className="max-h-[200px] max-w-full rounded-md"
                                    />
                                </div>
                            )
                        ) : (
                            <ImageUpload
                                onUpload={imageUpload.handleUpload}
                                onRemove={
                                    displayedImage
                                        ? handleRemoveImage
                                        : undefined
                                }
                                loading={imageUpload.loading}
                                error={imageUpload.error}
                                previewUrl={displayedImage}
                                label="Image (optional)"
                                helperText="Sent as a feed attachment at publish — separate from the text."
                            />
                        )}
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm text-muted-foreground">
                                Communities{' '}
                                <span className="text-destructive">*</span>
                            </span>
                            <CommunityMultiSelect
                                value={communities}
                                disabled={readOnly}
                                warningIds={missingCommunityIds}
                                successIds={memberCommunityIds}
                                onChange={(newValue) => {
                                    setCommunities(newValue);
                                    model.fields.communityIds.onChange(
                                        newValue.map((c) => c.id)
                                    );
                                }}
                            />
                            {model.fields.communityIds.error && (
                                <p className="text-xs text-destructive">
                                    {model.fields.communityIds.error}
                                </p>
                            )}
                            {membership && (
                                <Alert variant="warning">
                                    <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                                        <span>
                                            {model.fields.author.value?.name}{' '}
                                            isn&apos;t a member of{' '}
                                            {membership.missingCommunities
                                                .map((c) => c.name)
                                                .join(', ')}
                                            . You can&apos;t schedule until this
                                            is resolved — you can still save as
                                            a draft.
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="whitespace-nowrap"
                                            onClick={useGlobalAccountAsAuthor}
                                        >
                                            Post as Ms. Move (global)
                                        </Button>
                                    </AlertDescription>
                                </Alert>
                            )}
                            {!membership && membershipResolved && (
                                <p className="flex items-center gap-1 text-sm text-primary">
                                    <CircleCheck className="size-4" />
                                    All selected communities are covered by this
                                    account.
                                </p>
                            )}
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-sm text-muted-foreground">
                                Publish{' '}
                                <span className="text-destructive">*</span>
                            </span>
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="inline-flex rounded-md border p-0.5">
                                    {(
                                        [
                                            { value: 'now', label: 'Now' },
                                            {
                                                value: 'later',
                                                label: 'Schedule for later'
                                            }
                                        ] as const
                                    ).map((option) => (
                                        <Button
                                            key={option.value}
                                            type="button"
                                            size="sm"
                                            variant={
                                                model.fields.publishMode
                                                    .value === option.value
                                                    ? 'default'
                                                    : 'ghost'
                                            }
                                            disabled={readOnly}
                                            onClick={() =>
                                                model.fields.publishMode.onChange(
                                                    option.value
                                                )
                                            }
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                                {model.fields.publishMode.value === 'later' && (
                                    <Badge variant="outline">
                                        {
                                            Intl.DateTimeFormat().resolvedOptions()
                                                .timeZone
                                        }
                                    </Badge>
                                )}
                            </div>
                            {model.fields.publishMode.value === 'later' && (
                                <div className="mt-2">
                                    <DateTimePicker
                                        label="Publish at"
                                        value={model.fields.publishAt.value}
                                        disabled={readOnly}
                                        onChange={(value) =>
                                            model.fields.publishAt.onChange(
                                                value
                                            )
                                        }
                                        error={model.fields.publishAt.error}
                                        minDate={new Date()}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        {readOnly ? (
                            <Button variant="ghost" onClick={handleClose}>
                                Close
                            </Button>
                        ) : (
                            <>
                                {membership && (
                                    <span className="mr-auto text-xs text-muted-foreground">
                                        Resolve the membership issue to schedule
                                    </span>
                                )}
                                <Button
                                    variant="ghost"
                                    onClick={handleClose}
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => void handleSaveDraft()}
                                    disabled={submitting}
                                >
                                    Save draft
                                </Button>
                                <Button
                                    onClick={() => void handlePublish()}
                                    loading={submitting}
                                    disabled={submitting || Boolean(membership)}
                                >
                                    {membership && <Lock />}
                                    {model.fields.publishMode.value === 'now'
                                        ? 'Publish now'
                                        : 'Schedule'}
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
);
